/**
 * Pill-menu helpers — server-safe support for the streaming-style
 * cinematic-immersive header's pill rail.
 *
 * <p>Ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/pillMenu.ts}. Lives outside
 * the {@code 'use client'} component file because Next.js's RSC
 * boundary treats EVERY export from a {@code 'use client'} module as
 * a client reference — server components calling pure helpers from
 * such a module get undefined at runtime.
 */
import type { CurrentUser, LocalizedString } from '../../portal';
import { isVisible } from '../../portal';

export interface PillMenuItem {
  label: LocalizedString;
  href: string;
  badge?: LocalizedString;
  /** Static fallback flag from shell config — used by the client-side
   *  active picker when no menu item matches the current pathname. */
  active?: boolean;
}

/**
 * Filter a shell-config menu down to plain link items in serializable
 * shape. Drops menu groups and component menus — the pill rail doesn't
 * render them today.
 *
 * <p>Visibility filter (auth/perms) runs server-side because the shell
 * has access to the current user; the client component receives only
 * items the user is allowed to see.
 */
export function filterPillItems(
  menu: unknown[] | undefined,
  currentUser: CurrentUser | null,
): PillMenuItem[] {
  if (!Array.isArray(menu)) return [];
  return menu
    .filter(
      (m): m is Record<string, unknown> =>
        !!m &&
        typeof m === 'object' &&
        ((m as { kind?: unknown }).kind === 'link' ||
          (!(m as { kind?: unknown }).kind && !!(m as { href?: unknown }).href)),
    )
    .filter((m) =>
      isVisible(
        m as {
          requireAuth?: 'in' | 'out' | 'any';
          requirePerms?: string[];
          requireDynamicGroupIds?: number[];
          requireSecurityGroupIds?: number[];
        },
        currentUser,
      ),
    )
    .map((m) => ({
      label: m.label as LocalizedString,
      href: m.href as string,
      badge: m.badge as LocalizedString | undefined,
      active: m.active === true,
    }));
}
